import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Skeleton,
    CircularProgress,
    Alert,
    Stack,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import { DataGridPro, GridToolbarContainer } from '@mui/x-data-grid-pro';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from "../AxiosInstance";

const formatCurrency = (value) => {
    if (value === 0) return '£ -';
    return `£ ${value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatThreeDecimals = (value) => {
    if (value === null || value === undefined) return '-';
    if (value === 0) return '-';
    const truncated = Math.floor(value * 1000) / 1000;
    return truncated.toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
    });
};

const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const formatQuantity = (value) => {
    if (value === 0) return '-';
    return value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

function CustomToolbar({ selectedRows, onDelete }) {
    return (
        <GridToolbarContainer>
            {selectedRows.length > 0 && (
                <Button
                    startIcon={<DeleteIcon />}
                    onClick={onDelete}
                    color="error"
                    size="small"
                >
                    Delete Selected ({selectedRows.length})
                </Button>
            )}
        </GridToolbarContainer>
    );
}

function SalesOrdersGrid({ selectedMonth }) {
    const [salesOrders, setSalesOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 20,
    });
    const [sortModel, setSortModel] = useState([
        {
            field: 'orderDate',
            sort: 'desc',
        },
    ]);
    const [filterModel, setFilterModel] = useState({
        items: [],
        quickFilterValues: [],
    });
    const [rowCount, setRowCount] = useState(0);
    const [salesOrderLines, setSalesOrderLines] = useState({});
    const [loadingLines, setLoadingLines] = useState(new Set());
    const [selectedRows, setSelectedRows] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [rowsToDelete, setRowsToDelete] = useState([]);

    const fetchSalesOrders = async () => {
        try {
            setLoading(true);
            setError(null);

            // Prepare pagination parameters
            const params = {
                page: paginationModel.page + 1, // API expects 1-based page numbers
                pageSize: paginationModel.pageSize
            };

            // Add sorting parameters
            if (sortModel.length > 0) {
                params.sortField = sortModel[0].field;
                params.sortDirection = sortModel[0].sort;
            }

            // Add filter parameters
            if (filterModel.quickFilterValues && filterModel.quickFilterValues.length > 0) {
                params.search = filterModel.quickFilterValues.join(' ');
            }

            // Add column-specific filters
            if (filterModel.items && filterModel.items.length > 0) {
                filterModel.items.forEach((item, index) => {
                    if (item.value) {
                        params[`filter_${item.field}`] = item.value;
                        params[`filterOperator_${item.field}`] = item.operator || 'contains';
                    }
                });
            }

            // Sales orders grid shows ALL orders regardless of selected month
            
            const response = await axiosInstance.get('/salesorders', {
                params: params
            });
            
            setSalesOrders(response.data.salesOrders || []);
            setRowCount(response.data.totalCount || 0);
        } catch (error) {
            console.error("Failed to fetch sales orders:", error);
            setError("Failed to load sales orders. Please try again.");
            setSalesOrders([]);
            setRowCount(0);
        } finally {
            setLoading(false);
        }
    };

    const fetchSalesOrderLines = async (salesOrderId) => {
        try {
            setLoadingLines(prev => new Set(prev).add(salesOrderId));

            const response = await axiosInstance.get(`/salesorders/${salesOrderId}/lines`);
            
            setSalesOrderLines(prev => ({
                ...prev,
                [salesOrderId]: response.data || []
            }));
        } catch (error) {
            console.error(`Failed to fetch sales order lines for order ${salesOrderId}:`, error);
            setSalesOrderLines(prev => ({
                ...prev,
                [salesOrderId]: []
            }));
        } finally {
            setLoadingLines(prev => {
                const newSet = new Set(prev);
                newSet.delete(salesOrderId);
                return newSet;
            });
        }
    };

    const handleDeleteSelected = () => {
        setRowsToDelete(selectedRows);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        try {
            if (rowsToDelete.length === 1) {
                await axiosInstance.delete(`/salesorders/${rowsToDelete[0]}`);
            } else {
                await axiosInstance.post('/salesorders/delete-multiple', rowsToDelete, {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });
            }
            
            setDeleteDialogOpen(false);
            setSelectedRows([]);
            setRowsToDelete([]);
            fetchSalesOrders();
        } catch (error) {
            console.error("Failed to delete sales orders:", error);
            setError("Failed to delete sales orders. Please try again.");
            setDeleteDialogOpen(false);
        }
    };

    const cancelDelete = () => {
        setDeleteDialogOpen(false);
        setRowsToDelete([]);
    };

    useEffect(() => {
        fetchSalesOrders();
        // Clear expanded orders when filters change
        setSalesOrderLines({});
    }, [paginationModel, sortModel, filterModel, selectedMonth]);

    const columns = [
        {
            field: 'orderDate',
            headerName: 'Order Date',
            width: 120,
            type: 'date',
            sortable: true,
            filterable: true,
            valueGetter: (value) => value ? new Date(value) : null,
            valueFormatter: (value) => value ? formatDate(value) : '',
        },
        {
            field: 'requiredDate',
            headerName: 'Required Date',
            width: 120,
            type: 'date',
            sortable: true,
            filterable: true,
            valueGetter: (value) => value ? new Date(value) : null,
            valueFormatter: (value) => value ? formatDate(value) : '',
        },
        {
            field: 'providerSalesOrderId',
            headerName: 'Order Number',
            width: 150,
            sortable: false,
            filterable: true,
        },
        {
            field: 'customerName',
            headerName: 'Customer',
            width: 200,
            sortable: true,
            filterable: true,
        },
        {
            field: 'channelName',
            headerName: 'Channel',
            width: 150,
            sortable: false,
            filterable: false,
            valueGetter: (value) => value || 'Unlinked',
        },
        {
            field: 'orderStatus',
            headerName: 'Order Status',
            width: 130,
            sortable: true,
            filterable: true,
        },
        {
            field: 'totalValue',
            headerName: 'Total Value',
            width: 130,
            type: 'number',
            align: 'right',
            headerAlign: 'right',
            sortable: false,
            filterable: false,
            valueFormatter: (value) => formatCurrency(value),
        },
        {
            field: 'lineCount',
            headerName: 'Lines',
            width: 80,
            type: 'number',
            align: 'right',
            headerAlign: 'right',
            sortable: false,
            filterable: false,
        },
        {
            field: 'createdUtc',
            headerName: 'Created',
            width: 150,
            type: 'dateTime',
            sortable: false,
            filterable: false,
            valueGetter: (value) => value ? new Date(value) : null,
            valueFormatter: (value) => formatDateTime(value),
        },
        {
            field: 'updatedUtc',
            headerName: 'Updated',
            width: 150,
            type: 'dateTime',
            sortable: false,
            filterable: false,
            valueGetter: (value) => value ? new Date(value) : null,
            valueFormatter: (value) => formatDateTime(value),
        },
    ];

    const getDetailPanelContent = ({ row }) => {
        const lines = salesOrderLines[row.salesOrderId];
        const isLoading = loadingLines.has(row.salesOrderId);

        if (isLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            );
        }

        if (!lines || lines.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No order lines found.
                </Typography>
            );
        }

        return (
            <Stack sx={{ py: 2, height: 1, boxSizing: 'border-box' }} direction="column">
                <Paper sx={{ flex: 1, mx: 'auto', width: '95%', p: 1 }}>
                    <DataGridPro
                        rows={lines}
                        columns={[
                            {
                                field: 'productNumber',
                                headerName: 'SKU',
                                width: 120,
                                valueGetter: (value) => value || '-',
                            },
                            {
                                field: 'productDescription',
                                headerName: 'Description',
                                width: 250,
                            },
                            {
                                field: 'quantity',
                                headerName: 'Quantity',
                                width: 100,
                                type: 'number',
                                align: 'right',
                                headerAlign: 'right',
                                valueFormatter: (value) => formatQuantity(value),
                            },
                            {
                                field: 'packSize',
                                headerName: 'Pack Size',
                                width: 100,
                                type: 'number',
                                align: 'right',
                                headerAlign: 'right',
                                valueGetter: (value) => value ?? 1,
                            },
                            {
                                field: 'adjustedUnits',
                                headerName: 'Adjusted Units',
                                width: 130,
                                type: 'number',
                                align: 'right',
                                headerAlign: 'right',
                                sortable: false,
                                valueGetter: (_value, row) => (row.quantity ?? 0) * (row.packSize ?? 1),
                                valueFormatter: (value) => formatQuantity(value),
                            },
                            {
                                field: 'unitPrice',
                                headerName: 'Unit Price',
                                width: 100,
                                type: 'number',
                                align: 'right',
                                headerAlign: 'right',
                                valueFormatter: (value) => formatCurrency(value),
                            },
                            {
                                field: 'revenue',
                                headerName: 'Revenue',
                                width: 100,
                                type: 'number',
                                align: 'right',
                                headerAlign: 'right',
                                valueFormatter: (value) => formatCurrency(value),
                            },
                            {
                                field: 'cogs',
                                headerName: 'COGS',
                                width: 100,
                                type: 'number',
                                align: 'right',
                                headerAlign: 'right',
                                valueFormatter: (value) => formatThreeDecimals(value),
                            },
                            {
                                field: 'logs',
                                headerName: 'LOGS',
                                width: 100,
                                type: 'number',
                                align: 'right',
                                headerAlign: 'right',
                                valueFormatter: (value) => formatThreeDecimals(value),
                            },
                        ]}
                        getRowId={(row) => row.salesOrderLineId}
                        hideFooter
                        density="compact"
                        disableColumnMenu
                        disableRowSelectionOnClick
                    />
                </Paper>
            </Stack>
        );
    };

    const getDetailPanelHeight = ({ row }) => {
        const lines = salesOrderLines[row.salesOrderId];
        if (!lines) return 60;
        return Math.min(lines.length * 40 + 100, 400); // Max height of 400px
    };

    const handleDetailPanelExpandedRowIdsChange = (newExpandedRowIds) => {
        // Fetch lines for newly expanded rows
        newExpandedRowIds.forEach(salesOrderId => {
            if (!salesOrderLines[salesOrderId] && !loadingLines.has(salesOrderId)) {
                fetchSalesOrderLines(salesOrderId);
            }
        });
    };

    if (loading && salesOrders.length === 0) {
        return (
            <Box>
                <Skeleton variant="rectangular" width="100%" height={400} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', width: '100%' }}>
            <DataGridPro
                rows={salesOrders}
                columns={columns}
                getRowId={(row) => row.salesOrderId}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                filterModel={filterModel}
                onFilterModelChange={setFilterModel}
                rowCount={rowCount}
                loading={loading}
                pagination
                paginationMode="server"
                sortingMode="server"
                filterMode="server"
                pageSizeOptions={[10, 20, 50, 100]}
                checkboxSelection
                onRowSelectionModelChange={(newSelection) => {
                    setSelectedRows(newSelection);
                }}
                rowSelectionModel={selectedRows}
                getDetailPanelContent={getDetailPanelContent}
                getDetailPanelHeight={getDetailPanelHeight}
                onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
                slots={{
                    toolbar: () => (
                        <CustomToolbar
                            selectedRows={selectedRows}
                            onDelete={handleDeleteSelected}
                        />
                    ),
                }}
                sx={{
                    '& .MuiDataGrid-detailPanel': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    },
                }}
            />

            <Dialog
                open={deleteDialogOpen}
                onClose={cancelDelete}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete {rowsToDelete.length} sales order{rowsToDelete.length > 1 ? 's' : ''}? 
                        This will also delete all associated order lines. This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDelete} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} color="error" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default SalesOrdersGrid; 
